import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  List,
  ListItem,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';

import { useDymo } from '../contexts/DymoContext';
import { apiService } from '../services/api';
import { apiErrorMessage } from '../utils/apiError';
import type { Coil } from '../types';

interface KalotteCoilLabelDialogProps {
  chargeId: number;
  chargeNumber: string;
  open: boolean;
  onClose: () => void;
}

const KalotteCoilLabelDialog: React.FC<KalotteCoilLabelDialogProps> = ({
  chargeId,
  chargeNumber,
  open,
  onClose,
}) => {
  const dymo = useDymo();
  const ensureReady = dymo.ensureReady;
  const [coils, setCoils] = useState<Coil[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{
    message: string;
    severity: 'success' | 'warning' | 'error' | 'info';
  } | null>(null);

  useEffect(() => {
    if (!open) return;
    void ensureReady();
  }, [open, ensureReady]);

  // Load coils when dialog opens.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiService
      .getChargeCoils(chargeId)
      .then((list) => {
        if (cancelled) return;
        setCoils(list);
        setSelected(new Set(list.map((c) => c.id)));
      })
      .catch((err: any) => {
        if (cancelled) return;
        setError(apiErrorMessage(err, 'Coils konnten nicht geladen werden.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, chargeId]);

  const toggle = (coilId: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(coilId)) next.delete(coilId);
      else next.add(coilId);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected((prev) =>
      prev.size === coils.length ? new Set() : new Set(coils.map((c) => c.id))
    );
  };

  const handleClose = () => {
    if (printing) return;
    onClose();
  };

  const handlePrint = async () => {
    const toPrint = coils.filter((c) => selected.has(c.id));
    if (toPrint.length === 0) {
      setSnackbar({ message: 'Bitte mindestens einen Coil auswählen.', severity: 'info' });
      return;
    }
    setPrinting(true);
    try {
      const results = await dymo.printCoilLabels(
        toPrint.map((c) => ({
          chargeNumber,
          coilNumber: String(c.coil_number).padStart(3, '0'),
        }))
      );
      const failures = results.filter((r) => !r.success);
      if (failures.length === 0) {
        setSnackbar({
          message: `Alle ${results.length} Etiketten gedruckt.`,
          severity: 'success',
        });
        onClose();
      } else {
        setSnackbar({
          message: `${results.length - failures.length} von ${results.length} gedruckt — ${failures.length} fehlgeschlagen.`,
          severity: 'warning',
        });
      }
    } catch (err: any) {
      setSnackbar({
        message: err?.message ?? 'Druckvorgang fehlgeschlagen.',
        severity: 'error',
      });
    } finally {
      setPrinting(false);
    }
  };

  const dymoBlocked = dymo.isReady && !dymo.isAvailable;
  const noPrinter = dymo.isReady && dymo.isAvailable && !dymo.defaultPrinter;

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Etiketten drucken — Charge {chargeNumber}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            {!dymo.isReady && (
              <Typography variant="body2" color="text.secondary">
                Drucker wird gesucht …
              </Typography>
            )}
            {dymoBlocked && (
              <Alert severity="warning">
                DYMO Connect ist nicht verfügbar. Etiketten können von der Barcode-Seite manuell
                nachgedruckt werden.
              </Alert>
            )}
            {noPrinter && (
              <Alert severity="warning">
                Kein DYMO-Drucker gefunden — bitte anschließen und neu laden.
              </Alert>
            )}
            {loading ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={20} />
                <Typography variant="body2">Coils werden geladen…</Typography>
              </Stack>
            ) : error ? (
              <Alert severity="error">{error}</Alert>
            ) : coils.length === 0 ? (
              <Alert severity="info">
                Keine Coils gefunden — bitte Datenbank-Migration prüfen.
              </Alert>
            ) : (
              <>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selected.size === coils.length}
                      indeterminate={selected.size > 0 && selected.size < coils.length}
                      onChange={toggleAll}
                      disabled={printing}
                    />
                  }
                  label={`Alle ${coils.length} Coils`}
                />
                <List dense>
                  {coils.map((c) => (
                    <ListItem key={c.id} disablePadding>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selected.has(c.id)}
                            onChange={() => toggle(c.id)}
                            disabled={printing}
                          />
                        }
                        label={`Coil ${c.coil_number} — ${c.barcode}`}
                      />
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={printing}>
            Abbrechen
          </Button>
          <Button
            onClick={handlePrint}
            variant="contained"
            disabled={
              printing || loading || !dymo.isReady || coils.length === 0 || dymoBlocked || noPrinter
            }
            startIcon={printing ? <CircularProgress size={16} /> : undefined}
          >
            {printing ? 'Wird gedruckt…' : 'Drucken'}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar !== null}
        autoHideDuration={6000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar !== null ? (
          <Alert
            onClose={() => setSnackbar(null)}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
};

export default KalotteCoilLabelDialog;
